// SU special-units roster loader — v0M5.0 (2026-07-25).
//
// Loads the SpecialUnits table from src/data/generated/dials.json
// (produced by scripts/convert-xlsx.ts against docs/Balance.xlsx
// SpecialUnits sheet). At v1 the sheet ships with a single row (Spy);
// future entries (Engineer, Healer) land alongside the v0M7 world-
// buildables milestone.
//
// Special units are standalone map entities — distinct from Cohorts
// (which recruit into Armies) and from Army attachments (which attach
// to existing Armies). They have their own vision + abilities and are
// consumed by v0M5.3 spy entity + v0M5.4 infiltration systems.

import { assertSchemaVersion, BALANCE_SCHEMA_VERSION } from './loader';
import rawBlob from './generated/dials.json';

export interface SpecialUnitSpec {
  /** Free-text description — flavour + UI tooltip. */
  readonly description: string;
  /** Recruitment gold cost. */
  readonly goldCost: number;
  /** Days at the recruiting settlement to complete training. */
  readonly recruitTimeDays: number;
  /**
   * Settlement structure required to recruit. Empty string = no prereq.
   * Single structure name (not comma-separated) at v1 — matches Michael's
   * xlsx authoring pattern for the Spy row (`Thieves Guild`).
   */
  readonly structurePrereq: string;
  /**
   * Knowledge/tech prereq (matches KnowledgeTree.xlsx `Knowledge Name`).
   * Empty string = no prereq. Enforced by apply-commands.ts recruit_spy
   * (v0M5.3) against faction.researchedKnowledge.
   */
  readonly learningPrereq: string;
  /** Strategic-map march speed (cells/tick). Analogue of CohortSpec.moveSpeedCampaign. */
  readonly moveSpeedCampaign: number;
  /**
   * Vision radius (cells) contributed to owning faction's visibility map
   * per v0M5.1. Terrain-modified by the BFS LoS helper.
   */
  readonly visionRange: number;
  /**
   * Ability tags per v0M5-design. Recognized at v1: `Invisibility`
   * (hides the entity from viewers without Detection), `Detection`
   * (allows the entity to see Invisibility-tagged targets). Additional
   * tags land as new special units + structures earn them.
   */
  readonly abilities: readonly string[];
  /**
   * Per-day probability to detect + kill an enemy invisible entity in
   * the same settlement while performing Counter-Espionage (v0M5.4).
   * 0 for units without the mechanic. Spy default = 0.05.
   */
  readonly counterEspionageChance: number;
  /**
   * Days at each infiltration tier before upgrading (v0M5.4). 0 for
   * units without infiltration. Spy default = 10 (0 → Basic → Moderate
   * → Full over 30 days total).
   */
  readonly infiltrationDaysPerLevel: number;
  /**
   * Cap on infiltration levels reachable by this unit. 0 for units
   * without infiltration. Spy default = 3.
   */
  readonly maxInfiltrationLevel: number;
}

export type SpecialUnits = Readonly<Record<string, SpecialUnitSpec>>;

/** Load the special-units roster. Fails loudly on schemaVersion mismatch.
 *  Returns an empty object if the SpecialUnits sheet was absent from the
 *  xlsx at build time (retro-compat pattern matching BattleTerrain). */
export function loadSpecialUnits(): SpecialUnits {
  assertSchemaVersion(rawBlob, BALANCE_SCHEMA_VERSION, 'src/data/generated/dials.json');
  return (rawBlob as unknown as { specialUnits?: SpecialUnits }).specialUnits ?? {};
}

/** Retrieve a special unit by name; throw on unknown names.
 *  Uses the same lookup ergonomics as getCohort() / getStructure(). */
export function getSpecialUnit(units: SpecialUnits, name: string): SpecialUnitSpec {
  const entry = units[name];
  if (!entry) {
    throw new Error(
      `Unknown special unit: ${name}. Known: ${Object.keys(units).join(', ') || '(none)'}`,
    );
  }
  return entry;
}

/** Convenience: does this unit have the given ability tag? Case-sensitive
 *  match against the SpecialUnitSpec.abilities list. */
export function specialUnitHasAbility(spec: SpecialUnitSpec, ability: string): boolean {
  return spec.abilities.includes(ability);
}
